import React, { useState, useEffect, useCallback } from 'react';
import { getAdminClaims, approveClaim, rejectClaim } from '../../utils/api';
import Pagination from '../Pagination';
import CopyButton from '../CopyButton';

const ClaimsManagement = ({ config, flash, f, BC, SYM, fBC, vBC }) => {
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('pending');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [processing, setProcessing] = useState(null);

  const tr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '';

  const loadClaims = useCallback(async (status = 'pending', pg = 1) => {
    try {
      const res = await getAdminClaims(status, pg);
      setClaims(res.requests); setCounts(res.counts); setPagination(res.pagination);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadClaims(filter, 1); }, [filter, loadClaims]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve and auto-transfer funds to user wallet?')) return;
    setProcessing(id);
    try {
      const res = await approveClaim(id);
      flash(`Approved! ${res.message || 'Funds sent to user.'}`);
      loadClaims(filter, pagination.page);
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setProcessing(null);
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) { flash('Enter rejection reason', 'error'); return; }
    try {
      await rejectClaim(id, rejectReason);
      flash('Rejected, funds returned to user wallet');
      setRejectReason(''); setRejectingId(null);
      loadClaims(filter, pagination.page);
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  return (
    <div className="fade-in">
      <h2 className="adm-title">Claim Requests</h2>

      <div className="adm-kpi-grid cols-3">
        {['pending', 'approved', 'rejected'].map(s => (
          <div key={s} className={`adm-kpi card ${filter === s ? 'card-accent' : ''}`} onClick={() => setFilter(s)} style={{ cursor: 'pointer' }}>
            <span className="adm-kpi-label">{s.charAt(0).toUpperCase() + s.slice(1)}</span>
            <strong className="adm-kpi-val mono">{counts[s]}</strong>
          </div>
        ))}
      </div>

      {claims.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>User</th><th>Wallet</th><th>Amount</th><th>Date</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {claims.map(c => (
                <tr key={c._id}>
                  <td className="mono">{c.roboId}</td>
                  <td className="mono"><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{tr(c.walletAddress)}<CopyButton text={c.walletAddress} title="Copy wallet" size={12} /></span></td>
                  <td className="mono"><strong>{vBC(c.amount)}</strong></td>
                  <td>{new Date(c.requestedAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                  <td><span className={`d-status-badge badge-${c.status}`}>{c.status}</span></td>
                  <td>
                    {c.status === 'pending' && (
                      <div className="adm-claim-btns">
                        <button className="btn btn-primary btn-sm" onClick={() => handleApprove(c._id)} disabled={processing === c._id}>
                          {processing === c._id ? 'Sending...' : 'Proceed'}
                        </button>
                        {rejectingId === c._id ? (
                          <div className="adm-reject-inline">
                            <input className="input input-sm" placeholder="Reason..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ width: 150 }} />
                            <button className="btn btn-sm btn-outline danger" onClick={() => handleReject(c._id)}>Confirm</button>
                            <button className="btn btn-sm btn-outline" onClick={() => { setRejectingId(null); setRejectReason(''); }}>✕</button>
                          </div>
                        ) : (
                          <button className="btn btn-outline btn-sm danger" onClick={() => setRejectingId(c._id)}>Reject</button>
                        )}
                      </div>
                    )}
                    {c.status === 'approved' && c.txHash && <span className="mono text-3" style={{ fontSize: 10 }}>{tr(c.txHash)}</span>}
                    {c.status === 'rejected' && <span className="text-red" style={{ fontSize: 11 }}>{c.rejectionReason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div className="d-empty">No {filter} claims.</div>}

      <Pagination
        page={pagination.page}
        pages={pagination.pages}
        total={pagination.total}
        onPage={(p) => loadClaims(filter, p)}
      />
    </div>
  );
};

export default ClaimsManagement;